import React, { useState, useEffect } from 'react';
import { useSettings } from '../hooks/useSettings';
import { Spinner } from './Spinner';
import { SettingsManager, DraggableItem } from './SettingsManager';
import { SettingItem, Currency, SETTINGS_PAGE_SIZE_PREF_KEY } from '../types';
import { ConfirmModal } from './ConfirmModal';
import { dataManagementSections, DataManagementViewId } from '../config/adminConfig';
import { ManageableType } from '../contexts/SettingsContext';
import { ProductNormalizer } from './ProductNormalizer';
import { apiService } from '../services/apiService';

export const DataManagement: React.FC = () => {
    const [activeView, setActiveView] = useState<DataManagementViewId>(dataManagementSections[0].id as DataManagementViewId);
    const [renameConfirm, setRenameConfirm] = useState<{ oldName: string; newName: string } | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [pageSize, setPageSize] = useState(20);

    const {
        tags,
        categories,
        paymentMethods,
        currencies,
        businessNames,
        isLoading,
        addItem,
        addBusiness,
        updateItem,
        deleteItem,
        mergeItems,
        mergeBusiness,
        updateBusinessName,
        refresh,
    } = useSettings();
    
    useEffect(() => {
        if (isLoading === false) {
            setIsInitialLoading(false);
        }
    }, [isLoading]);

    useEffect(() => {
        const loadPageSize = async () => {
            const savedSize = await apiService.getPreference<number>(SETTINGS_PAGE_SIZE_PREF_KEY, 20);
            setPageSize(savedSize);
        };
        loadPageSize();
    }, []);

    useEffect(() => {
        refresh();
    }, [activeView, refresh]);

    const activeConfig = dataManagementSections.find(s => s.id === activeView);

    const NavButton: React.FC<{ view: DataManagementViewId; label: string; icon: React.ReactNode }> = ({ view, label, icon }) => (
        <button
            onClick={() => setActiveView(view)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeView === view ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
        >
            {icon}
            {label}
        </button>
    );

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        apiService.setPreference(SETTINGS_PAGE_SIZE_PREF_KEY, newSize);
    };

    const handleUpdate = async (item: DraggableItem) => {
        if (!activeConfig) return;

        if (activeConfig.id === 'businesses') {
            if (typeof item.id === 'string' && item.id !== item.name) {
                setRenameConfirm({ oldName: item.id, newName: item.name });
            }
        } else {
            await updateItem(activeConfig.id as ManageableType, item as SettingItem | Currency);
        }
    };

    const handleMerge = (sourceId: string | number, targetId: string | number) => {
        if (!activeConfig) return Promise.resolve();

        if (activeConfig.id === 'businesses') {
            // ID pro business je jeho název, takže sourceId a targetId jsou stringy
            return mergeBusiness(sourceId as string, targetId as string);
        } else {
            return mergeItems(activeConfig.id as ManageableType, sourceId as number, targetId as number);
        }
    };

    const handleDelete = (id: string | number) => {
        if (activeConfig && activeConfig.isDeletable) {
            deleteItem(activeConfig.id as ManageableType, id as number);
        }
    }

    const handleAdd = async (item: { name: string, color?: string }) => {
        if (activeConfig?.id === 'businesses') {
            await addBusiness(item.name);
        } else if (activeConfig && activeConfig.isAddable) {
            await addItem(activeConfig.id as ManageableType, item);
        }
    };

    const handleConfirmBusinessRename = async () => {
        if (renameConfirm) {
            await updateBusinessName(renameConfirm.oldName, renameConfirm.newName);
            setRenameConfirm(null);
        }
    };

    const getItemsForView = (): DraggableItem[] => {
        if (!activeConfig) return [];
        switch (activeConfig.id) {
            case 'tags': return tags;
            case 'categories': return categories;
            case 'paymentMethods': return paymentMethods;
            case 'currencies': return currencies;
            case 'businesses': return businessNames.map(b => ({ id: b.name, name: b.name, count: b.count }));
            default: return [];
        }
    }

    const renderActiveView = () => {
        if (!activeConfig) return null;
        if (activeConfig.type === 'custom') {
            switch(activeConfig.id) {
                case 'products':
                    return <ProductNormalizer />;
                default:
                    return <div>Neznámé vlastní zobrazení</div>;
            }
        }

        return (
            <SettingsManager
                key={activeConfig.id}
                title={activeConfig.managerTitle}
                items={getItemsForView()}
                onAddItem={handleAdd}
                onUpdateItem={handleUpdate}
                onDeleteItem={handleDelete}
                onMergeItems={handleMerge}
                hasColor={activeConfig.hasColor}
                isAddable={activeConfig.isAddable}
                isDeletable={activeConfig.isDeletable}
                showFilter={true}
                isRefreshing={isLoading}
                // FIX: Cast activeConfig.id to a type compatible with SettingsManager's activeViewId prop.
                // The logic above ensures 'products' view doesn't reach this component.
                activeViewId={activeConfig.id as ManageableType | 'businesses'}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />
        );
    };

    if (isInitialLoading) {
        return <Spinner text="Načítám nastavení..." />;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 border-b pb-4 flex-wrap">
                {dataManagementSections.map(section => (
                    <NavButton
                        key={section.id}
                        view={section.id as DataManagementViewId}
                        label={section.navLabel}
                        icon={React.createElement(section.icon, { className: 'w-5 h-5' })}
                    />
                ))}
            </div>

            {renderActiveView()}

            {renameConfirm && (
                <ConfirmModal
                    isOpen={!!renameConfirm}
                    onClose={() => setRenameConfirm(null)}
                    onConfirm={handleConfirmBusinessRename}
                    title="Přejmenovat podnik?"
                    confirmText="Přejmenovat"
                >
                    <p>Opravdu chcete přejmenovat podnik z <strong>"{renameConfirm.oldName}"</strong> na <strong>"{renameConfirm.newName}"</strong>? Tato změna se projeví u všech souvisejících účtenek.</p>
                </ConfirmModal>
            )}
        </div>
    );
};